import { VehicleIdentity, VehicleStateBadge, HealthBadge, LastSeen, SeverityBadge, AlertStateBadge } from './VehicleBits';
import { Badge, Pill } from '../ui/Badge';
import { alertTitle, roleMeta } from '../../lib/domain';
import { formatAttribute, attributeTone, attributeAlign, readAttribute } from '../../lib/attributes';
import * as fmt from '../../lib/format';

/**
 * Column definitions, shared.
 *
 * The dashboard shows a short vehicle list; the vehicles page shows the full
 * grid. Both are `<DataTable>` fed from here, so a column added once appears
 * correctly in both, and "speed" is formatted the same way wherever it lands.
 *
 * Each factory takes options rather than being duplicated per screen — pass
 * the subset you want and the widths follow.
 */

/**
 * Columns for attributes the platform discovered rather than declared.
 *
 * Each one renders from the `attributes` snapshot on the vehicle row, using the
 * type and unit the catalog recorded. That is what lets a customer put
 * `reefer_temp_c` on their grid without a line of code being written for it.
 */
export function dynamicColumns(selectedKeys, catalog) {
  if (!selectedKeys?.length || !catalog?.length) return [];
  const byKey = new Map(catalog.map((attribute) => [attribute.key, attribute]));

  return selectedKeys
    .map((key) => byKey.get(key))
    .filter(Boolean)
    .map((attribute) => ({
      key: `attr:${attribute.key}`,
      header: attribute.label,
      sortable: true,
      numeric: attribute.type === 'number',
      align: attributeAlign(attribute),
      value: (row) => readAttribute(row, attribute.key),
      render: (row) => {
        const value = readAttribute(row, attribute.key);
        const tone = attributeTone(attribute, value);
        const text = formatAttribute(attribute, value);
        return tone ? (
          <Badge tone={tone} size="sm">
            {text}
          </Badge>
        ) : (
          <span className={value === null || value === undefined ? 'text-ink-3' : ''}>{text}</span>
        );
      },
    }));
}

/**
 * The fleet grid.
 *
 * Three columns are structural and always present — who it is, what state it is
 * in, and when it last reported. Everything between them is chosen by the
 * person looking at it, from whatever their devices actually send. That is the
 * difference between a fixed report and a grid people can make their own.
 */
export function vehicleColumns({ compact = false, showAlerts = true, dynamic = [] } = {}) {
  const columns = [
    {
      key: 'reg_number',
      header: 'Vehicle',
      sortable: true,
      nowrap: false,
      render: (row) => <VehicleIdentity vehicle={row} showFleet={!compact} />,
    },
    {
      key: 'vehicle_state',
      header: 'Status',
      sortable: true,
      value: (row) => (row.online === false ? 'no_signal' : row.vehicle_state || 'parked'),
      render: (row) => <VehicleStateBadge vehicle={row} size="sm" />,
    },
  ];

  if (dynamic.length) {
    columns.push(...dynamic);
  } else {
    // Nothing chosen yet — show the readings that matter to almost everyone.
    columns.push({
      key: 'last_speed_kph',
      header: 'Speed',
      align: 'right',
      numeric: true,
      sortable: true,
      render: (row) => fmt.speed(row.last_speed_kph),
    });
  }

  if (!compact) {
    columns.push({
      key: 'health_score',
      header: 'Device health',
      sortable: true,
      numeric: true,
      render: (row) => (
        <HealthBadge
          score={row.health_score}
          online={row.online}
          flags={row.health_flags}
          showReasons
        />
      ),
    });
  }

  columns.push({
    key: 'last_packet_at',
    header: 'Last packet',
    sortable: true,
    align: compact ? 'right' : 'left',
    render: (row) => <LastSeen at={row.last_packet_at} online={row.online} />,
  });

  if (showAlerts && !compact) {
    columns.push({
      key: 'open_alerts',
      header: 'Alerts',
      align: 'right',
      numeric: true,
      sortable: true,
      render: (row) =>
        row.open_alerts > 0 ? (
          <Badge tone="critical" size="sm">
            {row.open_alerts}
          </Badge>
        ) : (
          <span className="text-ink-3">—</span>
        ),
    });
  }

  return columns;
}

export function deviceColumns() {
  return [
    {
      key: 'imei',
      header: 'IMEI',
      sortable: true,
      render: (row) => (
        <span className="font-mono text-[12.5px] text-ink">{row.imei || '—'}</span>
      ),
    },
    {
      key: 'reg_number',
      header: 'Fitted to',
      sortable: true,
      nowrap: false,
      render: (row) =>
        row.vehicle_id ? (
          <VehicleIdentity
            vehicle={{ id: row.vehicle_id, reg_number: row.reg_number, fleet_name: row.fleet_name }}
          />
        ) : (
          <Badge tone="neutral" size="sm" icon={null}>
            Spare
          </Badge>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => (
        <Badge tone={row.status === 'ACTIVE' ? 'good' : 'neutral'} size="sm" icon={null}>
          {row.status.toLowerCase()}
        </Badge>
      ),
    },
    {
      key: 'health_score',
      header: 'Health',
      sortable: true,
      numeric: true,
      render: (row) => <HealthBadge score={row.health_score} online={row.online} flags={row.health_flags} />,
    },
    {
      key: 'firmware_version',
      header: 'Firmware',
      sortable: true,
      render: (row) => <span className="font-mono text-[12.5px] text-ink-2">{row.firmware_version || '—'}</span>,
    },
    {
      key: 'hardware_model',
      header: 'Model',
      sortable: true,
      render: (row) => row.hardware_model || '—',
    },
    {
      key: 'last_seen_at',
      header: 'Last seen',
      sortable: true,
      render: (row) => <LastSeen at={row.last_packet_at || row.last_seen_at} online={row.online} />,
    },
  ];
}

export function alertColumns({ onAcknowledge } = {}) {
  return [
    {
      key: 'severity',
      header: 'Severity',
      sortable: true,
      render: (row) => <SeverityBadge severity={row.severity} />,
    },
    {
      key: 'kind',
      header: 'Alert',
      sortable: true,
      nowrap: false,
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-[13.5px] font-medium text-ink">{alertTitle(row)}</p>
          {row.message && <p className="truncate text-[12px] text-ink-3">{row.message}</p>}
        </div>
      ),
    },
    {
      key: 'reg_number',
      header: 'Vehicle',
      sortable: true,
      render: (row) =>
        row.vehicle_id ? (
          <VehicleIdentity
            vehicle={{ id: row.vehicle_id, reg_number: row.reg_number, fleet_name: row.fleet_name }}
          />
        ) : (
          '—'
        ),
    },
    { key: 'state', header: 'State', sortable: true, render: (row) => <AlertStateBadge state={row.state} /> },
    {
      key: 'opened_at',
      header: 'Opened',
      sortable: true,
      render: (row) => (
        <span className="text-[13px] text-ink-2" title={fmt.dateTime(row.opened_at)}>
          {fmt.relativeTime(row.opened_at)}
        </span>
      ),
    },
    ...(onAcknowledge
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right',
            render: (row) =>
              row.state === 'RESOLVED' ? null : (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onAcknowledge(row);
                  }}
                  className="rounded-md px-2 py-1 text-[12.5px] font-medium text-brand hover:bg-brand-soft"
                >
                  {row.state === 'OPEN' ? 'Acknowledge' : 'Resolve'}
                </button>
              ),
          },
        ]
      : []),
  ];
}

export function tripColumns() {
  return [
    {
      key: 'started_at',
      header: 'Started',
      sortable: true,
      render: (row) => fmt.dateTime(row.started_at),
    },
    {
      key: 'duration',
      header: 'Duration',
      sortable: true,
      numeric: true,
      value: (row) =>
        row.ended_at ? new Date(row.ended_at) - new Date(row.started_at) : null,
      render: (row) =>
        row.ended_at
          ? fmt.duration((new Date(row.ended_at) - new Date(row.started_at)) / 1000)
          : 'In progress',
    },
    {
      key: 'distance_km',
      header: 'Distance',
      align: 'right',
      numeric: true,
      sortable: true,
      render: (row) => fmt.distance(row.distance_km),
    },
    {
      key: 'avg_speed',
      header: 'Average',
      align: 'right',
      numeric: true,
      sortable: true,
      render: (row) => fmt.speed(row.avg_speed),
    },
    {
      key: 'max_speed',
      header: 'Top speed',
      align: 'right',
      numeric: true,
      sortable: true,
      render: (row) => fmt.speed(row.max_speed),
    },
    {
      key: 'idle_s',
      header: 'Idle',
      align: 'right',
      numeric: true,
      sortable: true,
      render: (row) => fmt.duration(row.idle_s),
    },
  ];
}

export function userColumns() {
  return [
    {
      key: 'full_name',
      header: 'Name',
      sortable: true,
      nowrap: false,
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-[13.5px] font-medium text-ink">{row.full_name}</p>
          <p className="truncate text-[12px] text-ink-3">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      render: (row) => {
        const meta = roleMeta(row.role);
        return (
          <Badge tone={meta.tone} size="sm" icon={null}>
            {meta.label}
          </Badge>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => (
        <Badge tone={row.status === 'ACTIVE' ? 'good' : 'neutral'} size="sm" icon={null}>
          {row.status.toLowerCase()}
        </Badge>
      ),
    },
    {
      key: 'last_login_at',
      header: 'Last signed in',
      sortable: true,
      render: (row) => (
        <span className="text-[13px] text-ink-2">{fmt.relativeTime(row.last_login_at)}</span>
      ),
    },
  ];
}

export function auditColumns() {
  return [
    {
      key: 'at',
      header: 'When',
      sortable: true,
      render: (row) => (
        <span className="text-[13px] text-ink-2" title={fmt.dateTime(row.at)}>
          {fmt.relativeTime(row.at)}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      sortable: true,
      render: (row) => <span className="font-mono text-[12.5px] text-ink">{row.action}</span>,
    },
    {
      key: 'actor_email',
      header: 'Who',
      sortable: true,
      render: (row) => row.actor_email || 'System',
    },
    {
      key: 'target_type',
      header: 'Target',
      sortable: true,
      render: (row) =>
        row.target_type ? (
          <span className="flex items-center gap-1.5">
            <Pill>{row.target_type}</Pill>
            <span className="font-mono text-[11.5px] text-ink-3">{fmt.maskId(row.target_id, 8, 4)}</span>
          </span>
        ) : (
          '—'
        ),
    },
    { key: 'ip', header: 'From', render: (row) => row.ip || '—' },
  ];
}
